import { Injectable } from '@angular/core';
import { STORAGE_KEYS } from '../enums/storage-keys.enum';

const PENDO_AGENT_SCRIPT_SELECTOR = 'script[data-pendo-agent]';

@Injectable({
  providedIn: 'root',
})
export class PendoService {
  initPendo(isPendoCookiesEnabled: boolean, apiKey: string): void {
    if (!this.isValidApiKey(apiKey)) {
      return;
    }

    void this.loadAgent(apiKey)
      .then(() => {
        const guid = localStorage.getItem(STORAGE_KEYS.pendoVisitorId) ?? crypto.randomUUID();
        localStorage.setItem(STORAGE_KEYS.pendoVisitorId, guid);

        (window as any).pendo.initialize({
          visitor: {
            id: guid,
          },
          disableCookies: !isPendoCookiesEnabled,
        });
      })
      .catch(() => {
        console.error('Failed to load Pendo agent script.');
      });
  }

  updatePendoCookies(isPendoCookiesEnabled: boolean): void {
    (window as any).pendo.updateOptions({ disableCookies: !isPendoCookiesEnabled });
  }

  revokePendoConsent(): void {
    if ((window as any).pendo?.isReady()) {
      (window as any).pendo?.stopGuides();
    }

    Object.keys(localStorage)
      .filter((name) => name.includes('pendo'))
      .forEach((entry) => localStorage.removeItem(entry));

    Object.keys(sessionStorage)
      .filter((name) => name.includes('pendo'))
      .forEach((entry) => sessionStorage.removeItem(entry));

    document.querySelectorAll(PENDO_AGENT_SCRIPT_SELECTOR).forEach((script) => script.remove());
  }

  private isValidApiKey(apiKey: string): boolean {
    return !!apiKey && !apiKey.startsWith('#{');
  }

  private ensurePendoStub(): void {
    const w = window as any;

    if (w.pendo) {
      return;
    }

    w.pendo = w.pendo || { _q: [] };
    const o = w.pendo;
    const methods = ['initialize', 'identify', 'updateOptions', 'pageLoad', 'track'];

    methods.forEach((method) => {
      o[method] =
        o[method] ||
        function (...args: unknown[]) {
          o._q[method === methods[0] ? 'unshift' : 'push']([method, ...args]);
        };
    });
  }

  private loadAgent(apiKey: string): Promise<void> {
    this.ensurePendoStub();

    const w = window as any;
    if (w.pendo?.isReady?.()) {
      return Promise.resolve();
    }

    const existingScript = document.querySelector(`script[data-pendo-agent="${apiKey}"]`) as HTMLScriptElement | null;
    if (existingScript?.dataset.pendoLoaded === 'true') {
      return Promise.resolve();
    }

    if (existingScript?.dataset.pendoLoadFailed === 'true') {
      existingScript.remove();
    } else if (existingScript) {
      return new Promise((resolve, reject) => {
        existingScript.addEventListener('load', () => resolve(), { once: true });
        existingScript.addEventListener(
          'error',
          () => {
            existingScript.dataset.pendoLoadFailed = 'true';
            reject(new Error('Pendo agent script failed to load'));
          },
          { once: true },
        );
      });
    }

    return this.injectAgentScript(apiKey);
  }

  private injectAgentScript(apiKey: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://cdn.eu.pendo.io/agent/static/${apiKey}/pendo.js`;
      script.dataset.pendoAgent = apiKey;
      script.addEventListener(
        'load',
        () => {
          script.dataset.pendoLoaded = 'true';
          resolve();
        },
        { once: true },
      );
      script.addEventListener(
        'error',
        () => {
          script.dataset.pendoLoadFailed = 'true';
          reject(new Error('Pendo agent script failed to load'));
        },
        { once: true },
      );

      const firstScript = document.getElementsByTagName('script')[0];
      firstScript?.parentNode?.insertBefore(script, firstScript);
    });
  }
}
